import _ from 'lodash';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LabeledInput from '../LabeledInput';
import { GroupInputs } from '../GroupInputs';
import { SelectInput } from '../SelectInput';

import * as api from '../../api';
import * as logger from '../../logger';

import defaultStyles from '../../styles/formSetStyles.module.css';
import { getOptionsFromRecord, getOptionsIntersection } from '../utils';
import { MultiEnum } from '../Question/MultiEnum';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  statuses: api.PatientSocialHistory.TobaccoStatus[];
  treatments: api.PatientSocialHistory.TobaccoTreatment[];
}

const MIN_AMOUNT = 0;
const MAX_AMOUNT = 100;
const EXCLUSIONARY_TREATMENT_OPTIONS: string[] = [
  api.PatientSocialHistory.TobaccoTreatment.None,
];

function formatEnumDisplay(value: string): string {
  return _.upperFirst(_.lowerCase(value));
}

export const TobaccoUsage: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
  statuses,
  treatments,
}) => {
  const { t } = useTranslation();
  const tobaccoUsageInit: Partial<api.PatientSocialHistory.TobaccoUsage> = {};
  const [tobaccoUsage, setTobaccoUsage] = useState(tobaccoUsageInit);

  useEffect(() => {
    try {
      if (!value) return;
      const parsedValue: api.PatientSocialHistory.TobaccoUsage =
        JSON.parse(value);
      setTobaccoUsage(parsedValue);
    } catch (e) {
      logger.error(e, 'Error parsing tobacco usage value');
    }
  }, [value]);

  const usageSelectAmountValues = getOptionsFromRecord(
    api.Previsit.tobaccoAmountMap,
    t,
  );

  if (tobaccoUsage.yearsOfUsage === undefined) {
    setTobaccoUsage({
      ...tobaccoUsage,
      yearsOfUsage: 0,
    });
  }

  const statusSelectValues = getOptionsIntersection(
    api.PatientSocialHistory.TobaccoStatus,
    statuses,
  ).map((status) => {
    return {
      display: api.Previsit.tobaccoStatusMap[status],
      key: status,
      value: status,
    };
  });

  const treatmentSelectValues = treatments.map((treatment) => ({
    display: t(formatEnumDisplay(treatment)),
    key: treatment,
    value: treatment,
  }));

  const tobaccoUsageUpdate = (update: {
    [key in keyof api.PatientSocialHistory.TobaccoUsage]?:
      | string
      | number
      | string[];
  }) => {
    if (
      update?.status === api.PatientSocialHistory.TobaccoStatus.Never ||
      update?.status === api.PatientSocialHistory.TobaccoStatus.Unknown
    ) {
      return JSON.stringify(update);
    }

    return JSON.stringify({ ...tobaccoUsage, ...update });
  };

  const saveFormField = (value: string) => {
    updateFieldValue(value);
    saveFieldValue(value);
  };

  if (tobaccoUsage.status && !statuses.includes(tobaccoUsage.status)) {
    const filteredTobaccoUsage = {
      ...tobaccoUsage,
      status: undefined,
    };
    saveFormField(JSON.stringify(filteredTobaccoUsage));
    setTobaccoUsage(filteredTobaccoUsage);
  }

  return (
    <GroupInputs>
      <LabeledInput label={t('Status')} htmlFor="tobacco-usage-status">
        <SelectInput
          id="tobacco-usage-status"
          value={tobaccoUsage.status || ''}
          onChange={(newValue: string) => {
            saveFormField(tobaccoUsageUpdate({ status: newValue }));
          }}
          options={statusSelectValues}
        />
      </LabeledInput>
      {tobaccoUsage.status &&
        tobaccoUsage.status !== api.PatientSocialHistory.TobaccoStatus.Never &&
        tobaccoUsage.status !==
          api.PatientSocialHistory.TobaccoStatus.Unknown && (
          <>
            <LabeledInput
              label={t('How many packs a day?')}
              htmlFor="tobacco-usage-amount"
            >
              <SelectInput
                id="tobacco-usage-amount"
                value={tobaccoUsage.amount || ''}
                onChange={(newValue: string) => {
                  saveFormField(tobaccoUsageUpdate({ amount: newValue }));
                }}
                options={usageSelectAmountValues}
              />
            </LabeledInput>
            <LabeledInput
              label={t('How many years did you use?')}
              htmlFor="tobacco-usage-years"
            >
              <div className={defaultStyles.InputWrapper}>
                <input
                  id="tobacco-usage-years"
                  className={defaultStyles.Input}
                  onChange={(event) => {
                    const newValue = +event.currentTarget.value;
                    if (
                      Number.isNaN(newValue) ||
                      newValue <= 0 ||
                      newValue > MAX_AMOUNT
                    ) {
                      saveFormField(tobaccoUsageUpdate({ yearsOfUsage: 0 }));
                      return;
                    }
                    saveFormField(
                      tobaccoUsageUpdate({ yearsOfUsage: newValue }),
                    );
                  }}
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  pattern="[0-9]*"
                  step={1}
                  type="number"
                  value={tobaccoUsage.yearsOfUsage}
                />
              </div>
            </LabeledInput>
            {!!treatments.length && (
              <LabeledInput
                label={t(
                  'Which of the following treatments for tobacco use have you previously completed?',
                )}
              >
                <MultiEnum
                  values={tobaccoUsage.previousTreatments ?? []}
                  options={treatmentSelectValues}
                  onItemClick={(treatment) => {
                    let previousTreatments: string[];
                    ({ previousTreatments = [] } = tobaccoUsage);

                    if (EXCLUSIONARY_TREATMENT_OPTIONS.includes(treatment)) {
                      return saveFormField(
                        tobaccoUsageUpdate({
                          previousTreatments: previousTreatments.includes(
                            treatment,
                          )
                            ? []
                            : [treatment],
                        }),
                      );
                    }

                    previousTreatments = previousTreatments.includes(treatment)
                      ? previousTreatments.filter((t) => t !== treatment)
                      : [...previousTreatments, treatment];

                    previousTreatments = previousTreatments.filter(
                      (t) => !EXCLUSIONARY_TREATMENT_OPTIONS.includes(t),
                    );

                    saveFormField(tobaccoUsageUpdate({ previousTreatments }));
                  }}
                  smallRowMargin={true}
                />
              </LabeledInput>
            )}
          </>
        )}
    </GroupInputs>
  );
};
